import type { QuestionWithResponse } from '@amzn/gbsa-helios-typescript-client';
import type {
  GetQuestionRequest,
  Question,
  SubQuestion,
} from 'src/types/helios/Question';
import { getLocalizedQuestionContent } from '../../../shared/domain';
import { deriveParentCompletion } from './deriveParentResult';

/** Pure render-time helpers for the mobile Perform Audit page. */

/**
 * Whether `alias` may answer this audit. An audit with no assignees
 * is treated as answerable by anyone who holds the perform
 * permission (the permission check is the caller's responsibility);
 * once assignees exist, only those aliases qualify. Case-insensitive.
 */
export function isUserAuditAssignee(
  assignees: readonly string[],
  alias: string,
): boolean {
  if (assignees.length === 0) return true;
  const normalized = alias.toLowerCase();
  return assignees.some((a) => a.toLowerCase() === normalized);
}

/** Inputs to {@link computePerformAuditReadOnly}. */
export interface PerformAuditReadOnlyInput {
  /** URL `mode=view` flag — a completed/expired audit opened read-only. */
  isViewMode: boolean;
  /** True when the audit status still permits editing (canManageAssignees). */
  isEditableStatus: boolean;
  /** Whether the auditor holds Perform*Audit permission for this type. */
  canPerformAudit: boolean;
  /** Whether the auditor is an assignee (see {@link isUserAuditAssignee}). */
  isCurrentUserAssignee: boolean;
}

/**
 * Whether the perform-audit answer surface is read-only. Read-only
 * when ANY of these hold: the URL requests view-mode, the status is
 * past editing, the auditor lacks the perform permission (PA-02), or
 * the auditor is not an assignee (PA-01).
 */
export function computePerformAuditReadOnly({
  isViewMode,
  isEditableStatus,
  canPerformAudit,
  isCurrentUserAssignee,
}: PerformAuditReadOnlyInput): boolean {
  return isViewMode || !isEditableStatus || !canPerformAudit || !isCurrentUserAssignee;
}

/**
 * Per-question fetch input from the summary row. Empty-string
 * fallbacks are safe — callers gate the fetch with `enabled`.
 */
export function buildQuestionQueryInput(
  currentSummary: QuestionWithResponse | undefined,
  businessUnit: string | undefined,
  language: string,
): GetQuestionRequest {
  const details = currentSummary?.questionDetails;
  return {
    programId: details?.program ?? '',
    businessUnit: businessUnit ?? '',
    divisionName: details?.divisionName ?? '',
    questionId: details?.questionId ?? '',
    ...(language ? { language } : {}),
    ...(details?.version ? { version: details.version } : {}),
  };
}

/** Completion metrics resolved for the sticky top nav. */
export interface AuditCompletionMetrics {
  /** Number of questions the auditor has responded to. */
  completed: number;
  /** Total questions denominator — the questions array length. */
  total: number;
  /** Rounded percentage in [0, 100]. Safe for divide-by-zero (0 when total is 0). */
  percent: number;
}

/**
 * Sticky-nav completion, derived client-side (server metrics are
 * eventual and can disagree with the badges). Mixed parents count as
 * completed via deriveParentCompletion — see the tests.
 */
export function computeAuditCompletionMetrics(
  questions: readonly QuestionWithResponse[],
): AuditCompletionMetrics {
  const total = questions.length;
  const completed = questions.filter(isQuestionCompleted).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, total, percent };
}

// Sub defs come from the summary payload (same source the
// close-audit validator reads) — no full fetch needed.
function isQuestionCompleted(q: QuestionWithResponse): boolean {
  const subDefs = q.questionDetails?.localizedContent?.content?.subQuestions;
  if (subDefs && subDefs.length > 0) {
    return deriveParentCompletion(
      q.responseDetails?.subQuestionResponses,
      subDefs.length,
    );
  }
  return q.responseDetails?.mainQuestionResponse?.responseResult != null;
}

/** Fields the perform-audit question card renders. */
export interface QuestionDisplayFields {
  questionId: string;
  title: string;
  questionText: string;
  instruction: string;
  passCriteria: string;
  failCriteria: string;
  justification: string;
  result: string | undefined;
}

/**
 * Question-card fields. Prefers the full question's localized content
 * (paired with presigned media); falls back to the summary payload so
 * text renders while the per-question fetch is in flight.
 */
export function computeQuestionDisplayFields(
  currentSummary: QuestionWithResponse | undefined,
  fullQuestion: Question | undefined,
  language: string,
): QuestionDisplayFields {
  const currentDetails = currentSummary?.questionDetails;
  const fullDetails = getLocalizedQuestionContent(
    fullQuestion,
    language,
  )?.questionDetails;
  const summaryDetails = currentDetails?.localizedContent?.content?.questionDetails;
  const displayDetails = fullDetails ?? summaryDetails;
  return {
    questionId: currentDetails?.questionId ?? '',
    title: displayDetails?.title ?? '',
    questionText: displayDetails?.questionText ?? '',
    instruction: displayDetails?.auditInstruction ?? '',
    passCriteria: displayDetails?.passCriteria ?? '',
    failCriteria: displayDetails?.failCriteria ?? '',
    justification: displayDetails?.justification ?? '',
    result: currentSummary?.responseDetails?.mainQuestionResponse?.responseResult,
  };
}

/** SubQuestion narrowed to a guaranteed id (React key + mutation target). */
export type DisplayableSubQuestion = SubQuestion & { subQuestionId: string };

/**
 * Localized, id-narrowed sub-questions for the current parent.
 * undefined = full question not loaded; [] = flat question.
 * Requirement flags are backfilled from the canonical defs —
 * translated packs carry display text only (see the tests).
 */
export function computeSubQuestionsForDisplay(
  fullQuestion: Question | undefined,
  language: string,
): DisplayableSubQuestion[] | undefined {
  if (!fullQuestion) return undefined;
  const localized = getLocalizedQuestionContent(fullQuestion, language)?.subQuestions;
  if (!localized) return [];
  const canonicalById = new Map<string, SubQuestion>();
  const canonical =
    fullQuestion.localizedContent?.content?.subQuestions ?? fullQuestion.subQuestions;
  for (const def of canonical ?? []) {
    if (def.subQuestionId) canonicalById.set(def.subQuestionId, def);
  }
  return localized
    .filter(
      (sq): sq is DisplayableSubQuestion =>
        typeof sq.subQuestionId === 'string' && sq.subQuestionId.length > 0,
    )
    .map((sq) => overlayRequirementFlags(sq, canonicalById.get(sq.subQuestionId)));
}

/** Backfill missing requirement flags; a localized flag wins when set. */
function overlayRequirementFlags(
  sq: DisplayableSubQuestion,
  canonical: SubQuestion | undefined,
): DisplayableSubQuestion {
  if (!canonical) return sq;
  // Conditional spreads keep absent props absent (exactOptionalPropertyTypes).
  const backfill = {
    ...(sq.requireCommentOnFail === undefined &&
    canonical.requireCommentOnFail !== undefined
      ? { requireCommentOnFail: canonical.requireCommentOnFail }
      : {}),
    ...(sq.requireCommentOnPass === undefined &&
    canonical.requireCommentOnPass !== undefined
      ? { requireCommentOnPass: canonical.requireCommentOnPass }
      : {}),
    ...(sq.requireEvidenceOnFail === undefined &&
    canonical.requireEvidenceOnFail !== undefined
      ? { requireEvidenceOnFail: canonical.requireEvidenceOnFail }
      : {}),
    ...(sq.requireEvidenceOnPass === undefined &&
    canonical.requireEvidenceOnPass !== undefined
      ? { requireEvidenceOnPass: canonical.requireEvidenceOnPass }
      : {}),
  };
  return { ...sq, ...backfill };
}
